/**
 * HVP Cache Manager (Frontend)
 *
 * Client-side caching with TTL support and LRU eviction
 * Prevents redundant API calls and improves performance
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { CacheSessionStoreMethods } from './cache-session-store.js';
import { CacheStatsMethods } from './cache-stats.js';

export class HVPCacheManager {
    /**
     * Constructor
     *
     * @param {Object} config - Configuration
     * @param {number} config.maxSize - Maximum cache entries
     * @param {number} config.defaultTtl - Default TTL in seconds
     * @param {boolean} config.debug - Debug mode
     * @param {boolean} config.sessionStorage - Enable sessionStorage persistence
     * @param {number} config.productId - Product ID for sessionStorage key
     */
    constructor(config = {}) {
        this.maxSize = config.maxSize || 100;
        this.defaultTtl = config.defaultTtl || 300; // 5 minutes
        this.debug = config.debug || false;

        // Session storage configuration
        this.sessionStorageEnabled = config.sessionStorage !== false;
        this.productId = config.productId || null;
        this.cacheVersion = '1.0.0'; // Increment on breaking changes
        this.sessionStorageTtl = config.sessionStorageTtl || 300000; // 5 minutes

        // Using Map for LRU ordering
        this.cache = new Map();
        this.metadata = new Map();

        // Save timeout for debouncing
        this.saveTimeout = null;

        // Statistics
        this.stats = {
            hits: 0,
            misses: 0,
            sets: 0,
            evictions: 0,
            expirations: 0,
            sessionRestores: 0,
            sessionSaves: 0
        };

        // Try to restore from sessionStorage on init
        if (this.sessionStorageEnabled && this.productId) {
            this.loadFromSessionStorage();
        }
    }

    /**
     * Get cached value
     *
     * @param {string} key - Cache key
     * @returns {*} Cached value or null
     */
    get(key) {
        if (!this.cache.has(key)) {
            this.stats.misses++;
            this.log('Cache miss:', key);
            return null;
        }

        const meta = this.metadata.get(key);

        // Check expiration
        if (meta.expiresAt && Date.now() > meta.expiresAt) {
            this.stats.expirations++;
            this.log('Cache expired:', key);
            this.delete(key);
            return null;
        }

        // Move to end (LRU)
        const value = this.cache.get(key);
        this.cache.delete(key);
        this.cache.set(key, value);

        // Update access time
        meta.lastAccess = Date.now();
        meta.hits++;

        this.stats.hits++;
        this.log('Cache hit:', key, `(${meta.hits} hits)`);

        return value;
    }

    /**
     * Set cached value
     *
     * @param {string} key - Cache key
     * @param {*} value - Value to cache
     * @param {number} [ttl] - TTL in seconds
     * @returns {boolean} Success
     */
    set(key, value, ttl = null) {
        // Evict if at max size
        if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
            this.evictOldest();
        }

        const actualTtl = ttl !== null ? ttl : this.defaultTtl;
        const expiresAt = actualTtl > 0 ? Date.now() + (actualTtl * 1000) : null;

        this.cache.set(key, value);
        this.metadata.set(key, {
            createdAt: Date.now(),
            lastAccess: Date.now(),
            expiresAt: expiresAt,
            hits: 0,
            size: this.estimateSize(value)
        });

        this.stats.sets++;
        this.log('Cache set:', key, `(TTL: ${actualTtl}s)`);

        // Schedule sessionStorage save
        this.scheduleSave();

        return true;
    }

    /**
     * Check if key exists and is valid
     *
     * @param {string} key - Cache key
     * @returns {boolean} True if exists and not expired
     */
    has(key) {
        if (!this.cache.has(key)) {
            return false;
        }

        const meta = this.metadata.get(key);

        // Check expiration
        if (meta.expiresAt && Date.now() > meta.expiresAt) {
            this.delete(key);
            return false;
        }

        return true;
    }

    /**
     * Delete cached value
     *
     * @param {string} key - Cache key
     * @returns {boolean} True if deleted
     */
    delete(key) {
        const existed = this.cache.delete(key);
        this.metadata.delete(key);

        if (existed) {
            this.log('Cache delete:', key);
        }

        return existed;
    }

    /**
     * Clear all cache
     *
     * @param {boolean} clearSession - Also clear sessionStorage (default: true)
     */
    clear(clearSession = true) {
        const size = this.cache.size;
        this.cache.clear();
        this.metadata.clear();

        // Also clear sessionStorage
        if (clearSession) {
            this.clearSessionStorage();
        }

        this.log('Cache cleared:', size, 'entries');
    }

    /**
     * Build cache key from components
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Current selections
     * @param {string} attribute - Target attribute
     * @returns {string} Cache key
     */
    buildKey(productId, selections = {}, attribute = '') {
        // Sort selections for consistent keys
        const sortedSelections = Object.keys(selections)
            .sort()
            .map(key => `${key}:${selections[key]}`)
            .join('|');

        return `${productId}:${attribute}:${sortedSelections}`;
    }

    /**
     * Evict oldest entry (LRU)
     */
    evictOldest() {
        // Map maintains insertion order, first key is oldest
        const oldestKey = this.cache.keys().next().value;

        if (oldestKey) {
            this.log('Cache evict (LRU):', oldestKey);
            this.delete(oldestKey);
            this.stats.evictions++;
        }
    }

    /**
     * Cleanup expired entries
     *
     * @returns {number} Number of expired entries removed
     */
    cleanup() {
        const now = Date.now();
        let removed = 0;

        for (const [key, meta] of this.metadata.entries()) {
            if (meta.expiresAt && now > meta.expiresAt) {
                this.delete(key);
                removed++;
            }
        }

        if (removed > 0) {
            this.log('Cache cleanup:', removed, 'expired entries');
        }

        return removed;
    }

    /**
     * Log message (debug mode only)
     *
     * @private
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP Cache]',
                'background: #8b5cf6; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
                ...args
            );
        }
    }
}

// Persistence + reporting live in sibling modules (350-line rule).
applyMixin(HVPCacheManager.prototype, CacheSessionStoreMethods);
applyMixin(HVPCacheManager.prototype, CacheStatsMethods);

export default HVPCacheManager;
